import { Box, Flex, ModalFooter, useToast } from "@chakra-ui/react";
import { ButtonCustom } from "../../form/controls/form.button";
import { Modal } from "../Modal";
import { useEffect, useState } from "react";
import { errorToast, successToast } from "../../../constants/toast.constants";
import { Loading } from "../../shared/Loading";
import { InventoryService } from "../../../service/inventory/inventory.service";
import { InventoryModel } from "../../../models/inventory.model";
import { InventoryCustomForm } from "../../shared/custom-forms/InventoryCustomForm";
import { convertIsoTime } from "../../../util/date.util";

export const EditInventoryModal = ({
  isOpen,
  onClose,
  onSubmit,
  inventoryId,
  formJson,
}) => {
  const inventoryService = new InventoryService();
  const toast = useToast();

  const [formState, setFormState] = useState({} as any);
  const [loading, setLoading] = useState(false);
  const [inventory, setInventory] = useState({} as InventoryModel);

  const onInputChange = (event: any) => {
    let { name, value } = event.target;

    if (value === "") value = null;

    if (name === "sanitisation") {
      return handleSanitisationChange(event);
    }

    if (name === "qaNotes") {
      setFormState({
        ...formState,
        qaNotes: value,
        qaNotesUpdatedDate: new Date().toISOString(),
      });
      
      return;
    }

    setFormState({
      ...formState,
      [name]: value,
    });
  };

  const handleSanitisationChange = (e: any) => {
    const { name, value } = e.target;

    if (!value) {
      setFormState({
        ...formState,
        sanitisation: null,
        destructionMethod: "",
        diskVerification: "",
        eraseMethod: "",
      });
      return;
    }

    if (value === "DESTROY") {
      setFormState({
        ...formState,
        sanitisation: value,
        destructionMethod: "SHRED",
        diskVerification: "PASS",
        eraseMethod: "SHREDDED",
      });
      return;
    }

    if (value === "CLEAR") {
      setFormState({
        ...formState,
        sanitisation: value,
        destructionMethod: "ERASE",
        diskVerification: "PASS",
        eraseMethod: "",
      });
      return;
    }

    setFormState({
      ...formState,
      sanitisation: value,
      destructionMethod: undefined,
      diskVerification: null,
    });
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setLoading(true);

    const obj = { ...inventory, ...formState, id: inventoryId };
    delete obj.copyCount;

    await inventoryService
      .update(obj)
      .then(() => {
        toast(successToast("Inventory updated."));
        handleClose();
        onSubmit();
      })
      .catch(() => {
        toast(errorToast("Error updating inventory."));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleClose = () => {
    setFormState({});
    setInventory({} as InventoryModel);
    onClose();
  };

  const init = async () => {
    const inventory = await inventoryService.get(inventoryId);
    setInventory(inventory);
    setFormState({ ...inventory, ...inventory.customFieldsJson });
  };

  useEffect(() => {
    setInventory({} as InventoryModel);
    if (inventoryId) {
      init();
    }
    return () => {};
  }, [inventoryId]);

  return (
    <Modal
      width="800px"
      isOpen={isOpen}
      header="Edit inventory item"
      onClose={handleClose}
    >
      <Loading loading={loading} fill ignoreDelay />
      <Box
        fontSize="sm"
        mb={4}
        border="1px solid lightgray"
        p={2}
        borderRadius="md"
        bg="gray.100"
      >
        <Box>
          Created on {convertIsoTime(inventory.createdDate)}{" "}
          {inventory.updatedDate
            ? `and updated on ${convertIsoTime(inventory.updatedDate)}`
            : ""}
        </Box>
        <Box hidden={!inventory.statusUpdatedDate}>
          {inventory.statusUpdatedDate
            ? `Status updated on ${convertIsoTime(inventory.statusUpdatedDate)}`
            : ""}
        </Box>
        <Box hidden={!inventory.qaNotesUpdatedDate}>
          {inventory.qaNotesUpdatedDate
            ? `QA notes updated on ${convertIsoTime(
                inventory.qaNotesUpdatedDate
              )}`
            : ""}
        </Box>
      </Box>

      <form onSubmit={handleSubmit}>
        <InventoryCustomForm
          formState={formState}
          json={formJson?.filter((x) => x.name !== "copyCount")}
          onInputChange={onInputChange}
        >
          <ModalFooter>
            <Flex gap="12px">
              <ButtonCustom secondary neutral onClick={onClose}>
                Cancel
              </ButtonCustom>
              <ButtonCustom type="submit">Confirm</ButtonCustom>
            </Flex>
          </ModalFooter>
        </InventoryCustomForm>
      </form>
    </Modal>
  );
};
